import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { classNames } from "../../utils";
import { Badge } from "../Badge";

interface TagsInputProps {
  label: string;
  error?: string;
  type?: string;
  name: string;
  placeholder?: string;
  defaultValue?: string[];
}

export function TagsInput({
  label,
  error,
  name,
  type = "text",
  placeholder,
  defaultValue,
}: TagsInputProps) {
  const [tag, setTag] = useState<string>("");
  // Drop blank entries saved by older versions (pressing Enter on an empty box).
  const [tags, setTags] = useState<string[]>(
    (defaultValue || []).filter((t) => t && t.trim() !== "")
  );

  const onEnter = () => {
    const value = tag.trim();
    // Ignore blanks and repeats.
    if (value && !tags.includes(value)) {
      setTags([...tags, value]);
    }
    setTag("");
  };

  const handleRemove = (index: number) => {
    setTags(tags.filter((_, idx) => idx !== index));
  };

  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative mt-1 rounded-md shadow-sm">
        <input
          id={name}
          type={type}
          onChange={(e) => setTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Stop Enter from also submitting the surrounding form.
              e.preventDefault();
              onEnter();
            }
          }}
          className={classNames(
            "block w-full rounded-md border-gray-300 pr-10 sm:text-sm",
            error
              ? "text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500"
              : ""
          )}
          placeholder={placeholder}
          value={tag}
        />
        {error && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600" id="error">
          {error}
        </p>
      )}
      {/* Wrap onto new lines so a long list stays inside the form (RP-2548). */}
      <div className="mt-1 flex flex-wrap gap-1">
        {tags.map((tag, idx) => (
          <div key={`${tag}-${idx}`}>
            {/* Outline so each tag stands out against the gray form background. */}
            <Badge
              removable
              onRemove={() => handleRemove(idx)}
              className="ring-1 ring-inset ring-gray-300"
            >
              {tag}
            </Badge>
          </div>
        ))}
      </div>
      <input name={name} type="hidden" value={tags.join(",")} />
    </div>
  );
}
